import { useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { CuboidCollider, RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import { CENTRAL_Y } from "../config/game";
import { threeColors } from "../theme/tokens";
import { useGameStore } from "../state/useGameStore";

const IDLE = new THREE.Vector3(1, 1, 1);
const HOVER = new THREE.Vector3(1.06, 1.06, 1.06);
const SQUASH = new THREE.Vector3(1.12, 0.88, 1.12);
const tmp = new THREE.Vector3();

const FACE_PLATES = [
  { pos: [0, 0, 0.795], args: [1.2, 1.2, 0.04] },
  { pos: [0, 0, -0.795], args: [1.2, 1.2, 0.04] },
  { pos: [0.795, 0, 0], args: [0.04, 1.2, 1.2] },
  { pos: [-0.795, 0, 0], args: [0.04, 1.2, 1.2] },
  { pos: [0, 0.795, 0], args: [1.2, 0.04, 1.2] },
  { pos: [0, -0.795, 0], args: [1.2, 0.04, 1.2] },
];

const EDGE_RAILS = [
  [0.78, 0.78, 0],
  [0.78, -0.78, 0],
  [-0.78, 0.78, 0],
  [-0.78, -0.78, 0],
];

/**
 * Hero mass - visual + input live outside Physics so the intro never waits on WASM.
 * A matching fixed collider is mounted inside the Physics world for pile contacts.
 */
export function CentralObject() {
  const group = useRef();
  const strike = useGameStore((s) => s.strike);
  const pulseToken = useGameStore((s) => s.pulseToken);
  const unlockedIds = useGameStore((s) => s.unlockedIds);
  const phase = useGameStore((s) => s.phase);
  const reducedMotion = useGameStore((s) => s.reducedMotion);
  const climaxActive = useGameStore((s) => s.climaxActive);
  const coarsePointer = useGameStore((s) => s.coarsePointer);

  const [hovered, setHovered] = useState(false);
  const squash = useRef(0);
  const lastPulse = useRef(0);
  const emissiveTarget = useRef(0.28);

  const coreMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: threeColors.metal,
        metalness: 0.62,
        roughness: 0.38,
        emissive: threeColors.metalMid,
        emissiveIntensity: 0.28,
      }),
    []
  );

  const plateMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#4a4c48",
        metalness: 0.45,
        roughness: 0.5,
        emissive: threeColors.darkGray,
        emissiveIntensity: 0.12,
      }),
    []
  );

  const railMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: threeColors.paper,
        metalness: 0.55,
        roughness: 0.35,
        emissive: threeColors.gray,
        emissiveIntensity: 0.08,
      }),
    []
  );

  const hasDensity = unlockedIds.includes("density");
  const hasCascade = unlockedIds.includes("cascade");
  const hasCore = unlockedIds.includes("core");
  const hasOrbit = unlockedIds.includes("orbit");
  const hasPulse = unlockedIds.includes("pulse");
  const hasVein = unlockedIds.includes("vein");

  useFrame((state, delta) => {
    if (!group.current) return;

    if (pulseToken !== lastPulse.current) {
      lastPulse.current = pulseToken;
      squash.current = 1;
    }

    squash.current = THREE.MathUtils.damp(squash.current, 0, 12, delta);

    const hoverScale = hovered && phase === "play" ? HOVER : IDLE;
    tmp.copy(hoverScale);
    if (squash.current > 0.01) {
      tmp.lerp(SQUASH, squash.current);
    }
    group.current.scale.lerp(tmp, 1 - Math.exp(-14 * delta));

    const spinRate = hasVein
      ? 0.55
      : hasOrbit
        ? 0.45
        : hasCore
          ? 0.35
          : 0.12;

    if (!reducedMotion && phase === "play") {
      group.current.rotation.y += delta * spinRate;
      group.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.4) * 0.04;
      if (hasOrbit) {
        group.current.rotation.z =
          Math.sin(state.clock.elapsedTime * 0.55) * 0.03;
      }
    }

    const baseEmissive = climaxActive
      ? 0.85
      : hasVein
        ? 0.62
        : hasPulse
          ? 0.56
          : hasOrbit
            ? 0.52
            : hasCore
              ? 0.5
              : hasDensity
                ? 0.38
                : hovered
                  ? 0.34
                  : 0.26;

    emissiveTarget.current = THREE.MathUtils.damp(
      emissiveTarget.current,
      baseEmissive + squash.current * (hasPulse ? 0.7 : 0.5),
      10,
      delta
    );

    const signalLit =
      climaxActive || hasDensity || hasOrbit || hasPulse || hasVein;

    coreMat.emissiveIntensity = Math.max(0.22, emissiveTarget.current);
    coreMat.color.set(signalLit ? threeColors.signal : threeColors.metal);
    coreMat.emissive.set(
      signalLit ? threeColors.signal : threeColors.metalMid
    );
    coreMat.metalness = hasCascade || hasVein ? 0.72 : 0.62;
    coreMat.roughness = hasVein ? 0.22 : hasCascade ? 0.28 : 0.38;

    plateMat.emissiveIntensity = Math.max(0.1, emissiveTarget.current * 0.55);
    plateMat.color.set(
      hasOrbit || hasVein
        ? threeColors.gray
        : hasDensity
          ? threeColors.paper
          : "#4a4c48"
    );
    plateMat.emissive.set(
      hasPulse || hasVein ? threeColors.signal : threeColors.darkGray
    );

    railMat.emissiveIntensity = hasVein
      ? Math.max(0.18, emissiveTarget.current * 0.4)
      : hasOrbit
        ? 0.14
        : 0.08;
    railMat.emissive.set(
      hasVein || hasOrbit ? threeColors.signal : threeColors.gray
    );
  });

  const interactive = phase === "play" && !climaxActive;

  const handleStrike = (e) => {
    e.stopPropagation();
    if (interactive) strike();
  };

  return (
    <group position={[0, CENTRAL_Y, 0]}>
      <group
        ref={group}
        onPointerDown={handleStrike}
        onPointerOver={(e) => {
          e.stopPropagation();
          if (interactive) {
            setHovered(true);
            document.body.style.cursor = "pointer";
          }
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = "default";
        }}
      >
        {/* Larger hit volume for coarse / touch - opacity 0 still raycasts */}
        {coarsePointer && (
          <mesh>
            <sphereGeometry args={[1.55, 12, 12]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        )}

        <mesh castShadow material={coreMat}>
          <boxGeometry args={[1.55, 1.55, 1.55]} />
        </mesh>

        {FACE_PLATES.map((face, i) => (
          <mesh key={i} position={face.pos} castShadow material={plateMat}>
            <boxGeometry args={face.args} />
          </mesh>
        ))}

        {EDGE_RAILS.map((pos, i) => (
          <mesh key={`r${i}`} position={pos} material={railMat}>
            <boxGeometry args={[0.06, 0.06, 1.55]} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/** Fixed collider only - mount inside `<Physics>`. */
export function CentralCollider() {
  return (
    <RigidBody type="fixed" colliders={false} position={[0, CENTRAL_Y, 0]}>
      <CuboidCollider args={[0.78, 0.78, 0.78]} restitution={0.15} friction={0.6} />
    </RigidBody>
  );
}
